const EXCLUDED_ENV_VARS = [
    'USE_MRT_NEW_VARIABLES',
    'IS_MULTI_SITE_ENVIRONMENT'
]
const {resolveBrandFromHost} = require('./brandUtils')

const getFallbackSiteId = (appConfig) => {
    return appConfig?.commerceAPI?.parameters?.siteId || appConfig?.defaultSite || null
}

const getSiteScopedValue = (appConfig, envVariable, siteId) => {
    if (!siteId) {
        return null
    }

    // Backward-compatible shape: appConfig[ENV_VAR][siteId]
    const fromScopedMap = appConfig?.[envVariable]?.[siteId]

    return fromScopedMap || null
}

const getSiteEnvVarValue = (appConfig, envVariable, siteId) => {
    if (!appConfig || !envVariable) {
        return undefined
    }

    const orgConfigKeys = appConfig?.GLOBAL_CONFIG_KEYS || []
    const isMultiSiteEnvironment = appConfig?.IS_MULTI_SITE_ENVIRONMENT === 'true'
    if (!isMultiSiteEnvironment || orgConfigKeys.indexOf(envVariable) > -1 || EXCLUDED_ENV_VARS.indexOf(envVariable) > -1) {
        const val = appConfig?.[envVariable]
        return typeof val === 'boolean' ? String(val) : val
    }

    const explicitOrDefaultSiteId = siteId || getFallbackSiteId(appConfig)
    const resolvedExplicitValue = getSiteScopedValue(
        appConfig,
        envVariable,
        explicitOrDefaultSiteId
    )

    if (resolvedExplicitValue !== undefined) {
        return typeof resolvedExplicitValue === 'boolean' ? String(resolvedExplicitValue) : resolvedExplicitValue
    }

    const val = appConfig?.[envVariable]
    return typeof val === 'boolean' ? String(val) : val
}

/**
 * Resolves a brand-scoped env var, preferring the brand-suffixed key
 * (`<ENV_VAR>_GARAGE` / `<ENV_VAR>_DYNAMITE`) and falling back to the un-suffixed
 * `<ENV_VAR>` when the brand-specific value is unset or empty.
 *
 * @param {Object} appConfig - The runtime app config (from getConfig().app).
 * @param {string} envVariable - Base env var name, e.g. 'IS_LOGROCKET_ENABLED'.
 * @param {string} siteId - The current site id.
 * @param {boolean} isGarage - Whether the current brand is Garage.
 */
const getBrandEnvVarValue = (appConfig, envVariable, siteId, isGarage) => {
    const brandSuffix = isGarage ? 'GARAGE' : 'DYNAMITE'
    const brandValue = getSiteEnvVarValue(appConfig, `${envVariable}_${brandSuffix}`, siteId)

    if (brandValue !== undefined && brandValue !== null && brandValue !== '') {
        return brandValue
    }

    return getSiteEnvVarValue(appConfig, envVariable, siteId)
}

const getBrandSiteEnvVarValue = (appConfig, envVariableValue, siteId) => {
    if (!appConfig || !envVariableValue) {
        return undefined
    }

    let brandValueMap = envVariableValue
    if (typeof envVariableValue === 'string') {
        try {
            const parsed = JSON.parse(envVariableValue)
            brandValueMap = parsed && typeof parsed === 'object' ? parsed : null
        } catch (_) {
            brandValueMap = null
        }
    }

    if (!brandValueMap || typeof brandValueMap !== 'object') {
        return undefined
    }

    const brandHostMap = getSiteEnvVarValue(appConfig, 'BRAND_HOST_MAP', siteId)
    const brandId = resolveBrandFromHost(brandHostMap)

    return brandValueMap[brandId.toLowerCase()]
}

/**
 * Resolves a value from a brand -> value map, e.g. {"garage": "…"}. Takes the
 * brand as an argument (unlike getBrandSiteEnvVarValue, which reads the browser
 * host) so it works server-side too. A plain, non-JSON value is returned as-is.
 */
const resolveBrandScopedValue = (envVariableValue, brandId) => {
    if (!envVariableValue) {
        return undefined
    }

    let brandValueMap = envVariableValue
    if (typeof envVariableValue === 'string') {
        try {
            const parsed = JSON.parse(envVariableValue)
            brandValueMap = parsed && typeof parsed === 'object' ? parsed : null
        } catch (_) {
            brandValueMap = null
        }
    }

    // Not a brand map (e.g. a plain string value) — return as-is.
    if (!brandValueMap || typeof brandValueMap !== 'object') {
        return typeof envVariableValue === 'string' ? envVariableValue : undefined
    }

    return brandValueMap[String(brandId || '').toLowerCase()]
}

/**
 * Resolves a value from a brand -> locale -> value map, e.g.
 * {"garage": {"en-us": "..."}, "dynamite": {"en-ca": "...", "fr-ca": "..."}}.
 *
 * The brand is resolved from the current host (via BRAND_HOST_MAP), and the
 * locale is matched case-insensitively against the provided locale id.
 *
 * If the value is not a brand/locale JSON map (e.g. a plain string), it is
 * returned as-is for backward compatibility.
 *
 * When `brandIdOverride` is provided it is used directly instead of resolving
 * the brand from the host. This is required on the server (SSR), where the host
 * is not available to `resolveBrandFromHost` and it would otherwise fall back to
 * the default brand — callers should pass the request-resolved brand (e.g. from
 * `useBrand()`).
 */
const getBrandLocaleSiteEnvVarValue = (appConfig, envVariableValue, siteId, locale, brandIdOverride) => {
    if (!appConfig || !envVariableValue) {
        return undefined
    }

    let brandLocaleMap = envVariableValue
    if (typeof envVariableValue === 'string') {
        try {
            const parsed = JSON.parse(envVariableValue)
            brandLocaleMap = parsed && typeof parsed === 'object' ? parsed : null
        } catch (_) {
            brandLocaleMap = null
        }
    }

    // Not a brand/locale map (e.g. a plain string value) — return as-is.
    if (!brandLocaleMap || typeof brandLocaleMap !== 'object') {
        return typeof envVariableValue === 'string' ? envVariableValue : undefined
    }

    const brandId =
        brandIdOverride || resolveBrandFromHost(getSiteEnvVarValue(appConfig, 'BRAND_HOST_MAP', siteId))
    const localeMap = brandLocaleMap[brandId.toLowerCase()]

    if (!localeMap || typeof localeMap !== 'object') {
        return undefined
    }

    const localeKey = String(locale || '').toLowerCase()
    return localeMap[localeKey]
}

/**
 * Convenience wrapper that reads a site-scoped env var by name and resolves it
 * against the current brand and the provided locale id.
 *
 * Pass `brandIdOverride` (e.g. `useBrand().brandId`) to resolve the brand
 * reliably on the server, where host-based detection is unavailable.
 */
const getBrandLocaleEnvVarValue = (appConfig, envVariable, siteId, locale, brandIdOverride) => {
    const value = getSiteEnvVarValue(appConfig, envVariable, siteId)
    return getBrandLocaleSiteEnvVarValue(appConfig, value, siteId, locale, brandIdOverride)
}

/**
 * Resolve the public storefront origin (e.g. https://www.grgclothing.com).
 *
 * Behind the exp-delivery proxy the request/forwarded host is the INTERNAL
 * origin (…exp-delivery.com), so neither `req.get('host')` nor
 * `useOrigin({fromXForwardedHeader: true})` yields the public host the browser
 * used. Instead we read the configured public domain from the
 * `PWA_DOMAIN_SITE_MULTI` brand map (the same source the SSR data hooks use),
 * which is set per environment (CI, staging, prod) so the resolved origin always
 * points at that environment's real public host.
 *
 * @param {Object} appConfig - The runtime app config (from getConfig().app).
 * @param {string} siteId - The current site id.
 * @param {string} brandId - The resolved brand id ('garage' | 'dynamite'). Should
 *   come from `useBrand()`, which resolves the brand via the configured
 *   `BRAND_HOST_MAP` on the client and the request host on the server — so it
 *   works even when the public domain does not literally contain the brand name
 *   (e.g. www.grgclothing.com -> garage).
 * @returns {string} The public origin, or a best-effort fallback.
 */
const resolvePublicOrigin = (appConfig, siteId, brandId) => {
    const domainMapRaw = getSiteEnvVarValue(appConfig, 'PWA_DOMAIN_SITE_MULTI', siteId)
    let domain
    if (domainMapRaw) {
        try {
            const domainMap =
                typeof domainMapRaw === 'string' ? JSON.parse(domainMapRaw) : domainMapRaw
            domain = domainMap?.[brandId] || domainMap?.garage
        } catch (_) {
            domain = undefined
        }
    }

    if (domain) {
        const protocol = domain.startsWith('localhost') ? 'http' : 'https'
        return `${protocol}://${domain}`
    }

    // Fallbacks: public origin on the client, configured app origin on the server.
    if (typeof window !== 'undefined') return window.location.origin
    return process.env.APP_ORIGIN || ''
}

/* Single source of truth for the Shop The Look source, so the PDP's
 * `expand=recommendations` and the carousel agree on server and client. */
const SHOP_THE_LOOK_SOURCE_SFCC = 'sfcc product recommendations'

const usesSfccShopTheLook = (appConfig, siteId) => {
    const value = getSiteEnvVarValue(appConfig, 'VICKY_SHOP_THE_LOOK_SOURCE', siteId)
    return typeof value === 'string' && value.trim().toLowerCase() === SHOP_THE_LOOK_SOURCE_SFCC
}

module.exports = {
    getSiteEnvVarValue,
    usesSfccShopTheLook,
    getFallbackSiteId,
    getBrandEnvVarValue,
    getBrandSiteEnvVarValue,
    resolveBrandScopedValue,
    getBrandLocaleSiteEnvVarValue,
    getBrandLocaleEnvVarValue,
    resolvePublicOrigin
}
